import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Send, Sparkles, Eye, X, Check, Plus, Trash2, Save, Settings, Repeat,
  PenLine, Archive, Play, Pause, Clock, CheckCircle2, Calendar, Loader2,
} from "lucide-react";
import { formatAdminDateTime } from "../utils/adminDateTime";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "digsby_admin_token";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const CADENCES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "bimonthly", label: "Every 2 months" },
];
const FONTS = [
  { label: "Quicksand (brand body)", value: "'Quicksand', Arial, sans-serif" },
  { label: "Bree Serif (brand heading)", value: "'Bree Serif', Georgia, serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
];
const SIZES = ["14", "15", "16", "18"];
const ALIGNS = ["left", "center", "right"];
const inputCls = "w-full min-h-[46px] rounded-xl border-2 border-black/15 px-4 font-medium outline-none focus:border-[#0000FF]";

function Field({ label, children }) {
  return <div><label className="block font-heading font-bold mb-2 text-sm">{label}</label>{children}</div>;
}

function useRecurringPaused() {
  const [paused, setPaused] = useState(null);
  const refresh = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/newsletter/settings`, authHeader()); setPaused(!!data.paused); }
    catch (e) { console.error("Failed to load recurring-paused status:", e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return [paused, refresh];
}

function RecurringStatusPill({ paused }) {
  if (paused === null) return null;
  return (
    <span data-testid="recurring-status-pill" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${paused ? "bg-orange-100 text-orange-700 border border-orange-300" : "bg-green-100 text-[#00A000] border border-green-300"}`}>
      <span className={`w-2 h-2 rounded-full ${paused ? "bg-orange-500" : "bg-[#00A000]"}`} />
      Recurring schedule: {paused ? "Paused" : "Active"}
    </span>
  );
}

function PreviewModal({ subject, html, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="nl-preview-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Eye size={20} /> Inbox Preview</h2>
            <p className="text-slate-500 text-sm mt-1">Subject: <span className="font-bold text-black">{subject}</span></p>
          </div>
          <button onClick={onClose} data-testid="nl-preview-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>
        <iframe title="preview" srcDoc={html} className="w-full h-[65vh] rounded-2xl border-2 border-black/10 bg-white" data-testid="nl-preview-frame" />
      </div>
    </div>
  );
}

function DiffReview({ diffs, accepted, setAccepted, onApply, onClose }) {
  const TYPE_BADGE = { generated: "bg-[#00EE00] text-black", revised: "bg-[#FBEF14] text-black", unchanged: "bg-slate-200 text-slate-600" };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="nl-diff-review">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Sparkles size={20} className="text-[#00A000]" /> Review AI Draft</h2>
          <button onClick={onClose} data-testid="nl-diff-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>
        <p className="text-slate-600 mb-5">Nothing changes until you accept it.</p>
        <div className="space-y-4">
          {diffs.map((d) => (
            <div key={d.field} className={`rounded-2xl border-2 p-4 ${d.type === "unchanged" ? "border-black/5 opacity-60" : "border-black/10"}`} data-testid={`nl-diff-${d.field}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-heading font-bold">{d.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${TYPE_BADGE[d.type]}`}>{d.type}</span>
                  {d.type !== "unchanged" && (
                    <label className="flex items-center gap-1 text-sm font-bold cursor-pointer">
                      <input type="checkbox" data-testid={`nl-diff-accept-${d.field}`} checked={!!accepted[d.field]} onChange={(e) => setAccepted((a) => ({ ...a, [d.field]: e.target.checked }))} className="w-4 h-4" /> Accept
                    </label>
                  )}
                </div>
              </div>
              {d.type !== "unchanged" && (
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-red-50 border border-red-100 p-3 whitespace-pre-line"><span className="block text-xs font-bold text-red-400 mb-1">BEFORE</span>{d.before || <em className="text-slate-400">(empty)</em>}</div>
                  <div className="rounded-xl bg-green-50 border border-green-100 p-3 whitespace-pre-line"><span className="block text-xs font-bold text-green-500 mb-1">AFTER</span>{d.after}</div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onApply} data-testid="nl-diff-apply" className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#00A000] text-white px-6 font-bold hover:bg-[#008800]"><Check size={18} /> Apply Selected</button>
          <button onClick={onClose} className="inline-flex items-center min-h-[48px] rounded-full border-2 border-black/15 px-6 font-bold hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsPanel({ handleErr }) {
  const [s, setS] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [saving, setSaving] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [pauseSaving, setPauseSaving] = useState(false);
  const [pauseError, setPauseError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/admin/newsletter/settings`, authHeader()),
        axios.get(`${API}/api/admin/newsletter/subscribers`, authHeader()),
      ]);
      setS(a.data); setStats(b.data);
    } catch (e) { handleErr(e); }
  }, [handleErr]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const addTopic = () => {
    const val = newTopic.trim();
    if (!val) { toast.error("Type a topic first, then click the plus to add it."); return; }
    set("topics", [...(s.topics || []), val]);
    setNewTopic("");
    toast.success("Topic added. Click Save Settings to keep it.");
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        cadence: s.cadence, paused: s.paused, topics: s.topics,
        mailing_address: s.mailing_address, reply_to: s.reply_to,
        proposal_notify_email: s.proposal_notify_email, next_due_at: s.next_due_at || null,
        alert_email: s.alert_email, alert_timezone: s.alert_timezone,
      };
      const { data } = await axios.put(`${API}/api/admin/newsletter/settings`, payload, authHeader());
      setS(data); toast.success("Settings saved.");
    } catch (e) { handleErr(e); } finally { setSaving(false); }
  };
  const togglePaused = async () => {
    const nextVal = !s.paused;
    setPauseSaving(true);
    setPauseError(null);
    try {
      const { data } = await axios.put(`${API}/api/admin/newsletter/settings`, { paused: nextVal }, authHeader());
      setS(data);
      toast.success(data.paused ? "Recurring schedule paused." : "Recurring schedule resumed.");
    } catch (e) {
      setPauseError("Couldn't save, try again.");
      handleErr(e);
    } finally {
      setPauseSaving(false);
    }
  };
  if (!s) return <p className="text-slate-500 py-10 text-center">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="nl-settings">
      <div className="grid grid-cols-3 gap-4">
        {[["Total", stats.total, "#0000FF"], ["Active", stats.active, "#00A000"], ["Unsubscribed", stats.unsubscribed, "#FF0000"]].map(([l, v, c]) => (
          <div key={l} className="rounded-2xl border-2 border-black/10 bg-white p-5">
            <div className="text-slate-500 font-bold text-sm">{l}</div>
            <div className="font-heading font-bold text-3xl mt-1" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3xl border-2 border-black/10 p-6 space-y-5">
        <h3 className="font-heading font-bold text-xl">Recurring schedule</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Cadence">
            <select className={inputCls} data-testid="nl-cadence" value={s.cadence} onChange={(e) => set("cadence", e.target.value)}>
              {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Next proposal prepared on">
            <input type="datetime-local" className={inputCls} data-testid="nl-next-due"
              value={s.next_due_at ? s.next_due_at.slice(0, 16) : ""} onChange={(e) => set("next_due_at", e.target.value)} />
          </Field>
        </div>
        <button onClick={togglePaused} disabled={pauseSaving} data-testid="nl-pause-toggle"
          className={`inline-flex items-center gap-2 min-h-[46px] rounded-full px-5 font-bold border-2 disabled:opacity-70 ${s.paused ? "bg-[#FBEF14] border-black text-black" : "bg-[#00A000] border-[#00A000] text-white"}`}>
          {pauseSaving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : s.paused ? <><Play size={16} /> Paused (click to resume)</> : <><Pause size={16} /> Active (click to pause)</>}
        </button>
        {pauseError && <p data-testid="nl-pause-error" className="text-sm font-bold text-red-600">{pauseError}</p>}
        <p className="text-sm text-slate-500">When paused, no new recurring cycles start. Your configuration is kept so you can resume anytime.</p>
      </div>

      <div className="bg-white rounded-3xl border-2 border-black/10 p-6 space-y-4">
        <h3 className="font-heading font-bold text-xl">Notifications &amp; compliance</h3>
        <Field label="Send proposal-ready alerts to (email)">
          <input className={inputCls} data-testid="nl-notify-email" value={s.proposal_notify_email || ""} onChange={(e) => set("proposal_notify_email", e.target.value)} />
        </Field>
        <Field label="Reply-to address">
          <input className={inputCls} data-testid="nl-replyto" value={s.reply_to || ""} onChange={(e) => set("reply_to", e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Send failure alerts to (email)">
            <input className={inputCls} data-testid="nl-alert-email" value={s.alert_email || ""} onChange={(e) => set("alert_email", e.target.value)} />
          </Field>
          <Field label="Alert timestamp timezone (IANA name)">
            <input className={inputCls} data-testid="nl-alert-timezone" value={s.alert_timezone || ""} onChange={(e) => set("alert_timezone", e.target.value)} placeholder="America/New_York" />
          </Field>
        </div>
        <p className="text-xs text-slate-500 -mt-2">You'll get an email here automatically whenever a Welcome, Manual, or Recurring send fails or partially fails.</p>
        <Field label="Physical mailing address (required in every email for CAN-SPAM)">
          <input className={inputCls} data-testid="nl-address" value={s.mailing_address || ""} onChange={(e) => set("mailing_address", e.target.value)} placeholder="[MAILING ADDRESS]" />
        </Field>
        <div className="rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-4">
          <p className="font-heading font-bold text-sm mb-1">Manage Your Subscription page</p>
          <p className="text-sm text-slate-600 mb-3">The public page linked in every email footer. Anyone can subscribe or unsubscribe here by entering their email.</p>
          <a href={`${API}/api/newsletter/unsubscribe`} target="_blank" rel="noopener noreferrer" data-testid="nl-unsub-link"
            className="inline-flex items-center gap-2 min-h-[42px] rounded-full border-2 border-[#0000FF] text-[#0000FF] px-4 font-bold hover:bg-[#0000FF] hover:text-white">
            <Mail size={15} /> Open Manage Your Subscription page
          </a>
        </div>
      </div>

      <div className="bg-white rounded-3xl border-2 border-black/10 p-6 space-y-3">
        <h3 className="font-heading font-bold text-xl">Approved research topics</h3>
        <p className="text-sm text-slate-500">Recurring newsletter topics are chosen only from this list (or a custom topic you type per cycle).</p>
        <div className="space-y-2">
          {(s.topics || []).map((t, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputCls} data-testid={`nl-topic-${i}`} value={t} onChange={(e) => set("topics", s.topics.map((x, j) => j === i ? e.target.value : x))} />
              <button onClick={() => set("topics", s.topics.filter((_, j) => j !== i))} data-testid={`nl-topic-remove-${i}`} className="rounded-xl border-2 border-black/15 px-3 hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input className={inputCls} data-testid="nl-topic-new" value={newTopic} onChange={(e) => setNewTopic(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }}
              placeholder="Add a topic tied to your catalog or a milestone" />
            <button type="button" onClick={addTopic} data-testid="nl-topic-add" className="rounded-xl border-2 border-black/15 px-3 font-bold hover:bg-[#FBEF14]"><Plus size={16} /></button>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="nl-settings-save" className="inline-flex items-center gap-2 min-h-[50px] rounded-full bg-[#0000FF] text-white px-8 font-bold hover:bg-[#0000d6] disabled:opacity-60"><Save size={18} /> {saving ? "Saving…" : "Save Settings"}</button>
    </div>
  );
}

/* ---------- Welcome ---------- */
function WelcomePanel({ handleErr }) {
  const [w, setW] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [diffs, setDiffs] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [preview, setPreview] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/newsletter/welcome`, authHeader());
      setW(data);
      const src = data.draft || data.active || {};
      setSubject(src.subject || ""); setBody(src.body || ""); setDirty(!!data.draft);
    } catch (e) { handleErr(e); }
  }, [handleErr]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/newsletter/welcome/generate`, {}, authHeader());
      const acc = {}; data.diffs.forEach((d) => { acc[d.field] = d.type !== "unchanged"; });
      setDiffs(data.diffs); setAccepted(acc);
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const applyDiffs = () => {
    diffs.forEach((d) => { if (!accepted[d.field]) return; if (d.field === "subject") setSubject(d.after); if (d.field === "body") setBody(d.after); });
    setDiffs(null); setDirty(true); toast.success("Applied. Review, then Save Draft and Activate.");
  };
  const saveDraft = async () => {
    setBusy(true);
    try { await axios.put(`${API}/api/admin/newsletter/welcome`, { subject, body }, authHeader()); toast.success("Draft saved."); setDirty(true); await load(); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const activate = async () => {
    if (!window.confirm("Activate this template? It becomes the email sent to every new signup going forward.")) return;
    setBusy(true);
    try { await axios.put(`${API}/api/admin/newsletter/welcome`, { subject, body }, authHeader());
      await axios.post(`${API}/api/admin/newsletter/welcome/activate`, {}, authHeader());
      toast.success("Welcome email activated."); await load(); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const openPreview = async () => {
    try { await axios.put(`${API}/api/admin/newsletter/welcome`, { subject, body }, authHeader());
      const { data } = await axios.get(`${API}/api/admin/newsletter/welcome/preview`, authHeader()); setPreview(data); }
    catch (e) { handleErr(e); }
  };
  if (!w) return <p className="text-slate-500 py-10 text-center">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="nl-welcome">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`rounded-full px-3 py-1.5 text-xs font-bold ${w.activated && !dirty ? "bg-[#00EE00] text-black" : "bg-[#FBEF14] text-black"}`} data-testid="nl-welcome-status">
          {dirty ? "Unactivated changes" : w.activated ? "Active" : "Not activated"}
        </span>
        <span className="text-sm text-slate-500">This one approved template is reused for every new signup (AI is not called per signup).</span>
      </div>
      <div className="bg-white rounded-3xl border-2 border-black/10 p-6 space-y-4">
        <Field label="Subject line"><input className={inputCls} data-testid="nl-welcome-subject" value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} /></Field>
        <Field label="Email body (blank line between paragraphs)"><textarea rows={10} className={`${inputCls} py-3 resize-y`} data-testid="nl-welcome-body" value={body} onChange={(e) => { setBody(e.target.value); setDirty(true); }} /></Field>
        <div className="rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-4">
          <button onClick={generate} disabled={busy} data-testid="nl-welcome-generate" className="inline-flex items-center gap-2 min-h-[46px] rounded-full bg-[#00A000] text-white px-5 font-bold hover:bg-[#008800] disabled:opacity-60"><Sparkles size={16} /> {busy ? "Working…" : "Generate Welcome Email (AI)"}</button>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={openPreview} data-testid="nl-welcome-preview" className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-black/15 px-5 font-bold hover:bg-[#FBEF14]"><Eye size={16} /> Preview</button>
          <button onClick={saveDraft} disabled={busy} data-testid="nl-welcome-save" className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-black px-5 font-bold hover:bg-[#FBEF14] disabled:opacity-60"><Save size={16} /> Save Draft</button>
          <button onClick={activate} disabled={busy} data-testid="nl-welcome-activate" className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#0000FF] text-white px-6 font-bold hover:bg-[#0000d6] disabled:opacity-60"><CheckCircle2 size={16} /> Activate</button>
        </div>
      </div>
      {diffs && <DiffReview diffs={diffs} accepted={accepted} setAccepted={setAccepted} onApply={applyDiffs} onClose={() => setDiffs(null)} />}
      {preview && <PreviewModal subject={preview.subject} html={preview.html} onClose={() => setPreview(null)} />}
    </div>
  );
}

/* ---------- Shared draft editor (recurring draft + manual) ---------- */
function DraftEditor({ item, books, onBack, reload, handleErr }) {
  const [d, setD] = useState(item);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedAt, setSchedAt] = useState("");
  const [pasteMode, setPasteMode] = useState(!!item.paste_html);
  const [sendError, setSendError] = useState(
    (item.status === "failed" || item.status === "partial") && item.send_error ? item.send_error : null
  );
  const [paused] = useRecurringPaused();

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setSection = (i, k, v) => setD((p) => ({ ...p, sections: p.sections.map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const fmt = d.formatting || {};
  const setFmt = (k, v) => setD((p) => ({ ...p, formatting: { ...(p.formatting || {}), [k]: v } }));

  const payload = () => ({
    subject: d.subject || "", intro: d.intro || "", sections: d.sections || [],
    featured_slugs: d.featured_slugs || [], formatting: d.formatting || {}, paste_html: pasteMode ? (d.paste_html || "") : "",
  });
  const persist = async () => { const { data } = await axios.put(`${API}/api/admin/newsletter/item/${d.id}`, payload(), authHeader()); return data; };
  const save = async () => { setBusy(true); try { await persist(); toast.success("Saved."); reload(); } catch (e) { handleErr(e); } finally { setBusy(false); } };
  const regenerate = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/api/admin/newsletter/item/${d.id}/generate-draft`, {}, authHeader()); setD(data); toast.success("Draft regenerated."); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const aiBlock = async (kind, i) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/newsletter/ai-block`, { kind, topic: d.approved_topic || d.subject || "early learning", context: d.intro || "" }, authHeader());
      if (kind === "subject") set("subject", data.text);
      else if (kind === "intro") set("intro", data.text);
      else setSection(i, "text", data.text);
      toast.success("AI text added.");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const openPreview = async () => { try { await persist(); const { data } = await axios.get(`${API}/api/admin/newsletter/item/${d.id}/preview`, authHeader()); setPreview(data); } catch (e) { handleErr(e); } };
  const sendNow = async () => {
    if (!window.confirm("Approve and send to your entire active subscriber list now?")) return;
    setBusy(true);
    setSendError(null);
    try {
      await persist();
      const { data } = await axios.post(`${API}/api/admin/newsletter/item/${d.id}/send`, {}, authHeader());
      if (data.status === "partial") {
        setSendError(`Partially sent: ${data.error_reason || `${data.failed_count} recipient(s) failed.`}`);
        toast.error("Some recipients did not receive this send. See details below.");
        reload();
      } else {
        toast.success(`Sent to ${data.success_count ?? "your"} subscriber(s).`);
        onBack(); reload();
      }
    } catch (e) {
      setSendError(e.response?.data?.detail || "Send failed. No email was delivered.");
      handleErr(e);
      reload();
    } finally { setBusy(false); }
  };
  const schedule = async () => {
    if (!schedAt) return toast.error("Pick a date and time.");
    setBusy(true);
    try {
      await persist();
      // datetime-local gives a naive "local wall-clock" string with no timezone info;
      // convert it to a real UTC instant here so it isn't mistakenly stored as if it
      // were already UTC (which double-shifts it on every later local-time display).
      const utcIso = new Date(schedAt).toISOString();
      await axios.post(`${API}/api/admin/newsletter/item/${d.id}/send`, { scheduled_for: utcIso }, authHeader());
      if (d.type === "recurring" && paused) {
        toast.warning("This recurring schedule is currently Paused. Your changes are saved, but no sends will go out until you resume it.", { duration: 8000 });
      } else {
        toast.success("Scheduled.");
      }
      onBack(); reload();
    }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="nl-draft-editor">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-bold text-slate-600 hover:text-[#0000FF]">← Back</button>
        <RecurringStatusPill paused={paused} />
      </div>
      {d.type === "recurring" && paused && (
        <div data-testid="nl-recurring-paused-banner" className="rounded-2xl border-2 border-orange-400 bg-orange-50 p-4">
          <p className="font-bold text-orange-700">This recurring schedule is currently Paused</p>
          <p className="text-sm text-orange-600 mt-0.5">Your changes are saved, but no sends will go out until you resume it in Settings.</p>
        </div>
      )}
      {d.type === "manual" && (
        <p data-testid="nl-manual-recurring-context" className="text-sm text-slate-500">
          Manual send always attempts delivery right now, independent of the recurring schedule.
        </p>
      )}
      {sendError && (
        <div data-testid="nl-send-error-banner" className="rounded-2xl border-2 border-red-500 bg-red-50 p-4 flex items-start justify-between gap-3">
          <div>
            <p className="font-bold text-red-700">Send failed - no email was delivered</p>
            <p className="text-sm text-red-600 mt-0.5">{sendError}</p>
          </div>
          <button onClick={() => setSendError(null)} data-testid="nl-send-error-dismiss" className="text-red-600 hover:text-red-800 shrink-0"><X size={18} /></button>
        </div>
      )}
      {d.approved_topic && <p className="text-sm text-slate-500">Topic: <span className="font-bold text-black">{d.approved_topic}</span></p>}

      <div className="bg-white rounded-3xl border-2 border-black/10 p-6 space-y-4">
        <Field label="Subject line">
          <div className="flex gap-2">
            <input className={inputCls} data-testid="nl-edit-subject" value={d.subject || ""} onChange={(e) => set("subject", e.target.value)} />
            <button onClick={() => aiBlock("subject")} title="Generate with AI" className="rounded-xl border-2 border-[#00A000] text-[#00A000] px-3 hover:bg-[#00A000] hover:text-white" data-testid="nl-ai-subject"><Sparkles size={16} /></button>
          </div>
        </Field>

        <label className="flex items-center gap-2 font-bold text-sm">
          <input type="checkbox" checked={pasteMode} onChange={(e) => setPasteMode(e.target.checked)} data-testid="nl-paste-toggle" className="w-4 h-4" /> Paste raw HTML / text instead of blocks
        </label>

        {pasteMode ? (
          <Field label="Pasted content (HTML or text)"><textarea rows={10} className={`${inputCls} py-3 resize-y font-mono text-sm`} data-testid="nl-paste-html" value={d.paste_html || ""} onChange={(e) => set("paste_html", e.target.value)} /></Field>
        ) : (
          <>
            <Field label="Intro">
              <div className="flex gap-2 items-start">
                <textarea rows={4} className={`${inputCls} py-3 resize-y`} data-testid="nl-edit-intro" value={d.intro || ""} onChange={(e) => set("intro", e.target.value)} />
                <button onClick={() => aiBlock("intro")} title="Generate with AI" className="rounded-xl border-2 border-[#00A000] text-[#00A000] px-3 py-3 hover:bg-[#00A000] hover:text-white" data-testid="nl-ai-intro"><Sparkles size={16} /></button>
              </div>
            </Field>
            <div className="space-y-3">
              {(d.sections || []).map((s, i) => (
                <div key={i} className="rounded-2xl border-2 border-black/10 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-500">Section {i + 1}</span>
                    <button onClick={() => set("sections", d.sections.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 rounded-lg p-1" data-testid={`nl-section-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                  <input className={inputCls} placeholder="Section heading (optional)" data-testid={`nl-section-heading-${i}`} value={s.heading || ""} onChange={(e) => setSection(i, "heading", e.target.value)} />
                  <div className="flex gap-2 items-start">
                    <textarea rows={3} className={`${inputCls} py-3 resize-y`} placeholder="Section text" data-testid={`nl-section-text-${i}`} value={s.text || ""} onChange={(e) => setSection(i, "text", e.target.value)} />
                    <button onClick={() => aiBlock("body", i)} title="Generate with AI" className="rounded-xl border-2 border-[#00A000] text-[#00A000] px-3 py-3 hover:bg-[#00A000] hover:text-white" data-testid={`nl-ai-section-${i}`}><Sparkles size={16} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => set("sections", [...(d.sections || []), { heading: "", text: "" }])} data-testid="nl-section-add" className="inline-flex items-center gap-1 rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-[#FBEF14]"><Plus size={15} /> Add section</button>
            </div>

            <Field label="Featured catalog titles">
              <div className="grid sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto rounded-2xl border-2 border-black/10 p-3">
                {books.map((b) => {
                  const on = (d.featured_slugs || []).includes(b.slug);
                  return (
                    <label key={b.id} className={`flex items-center gap-2 rounded-xl border-2 p-2 cursor-pointer text-sm font-medium ${on ? "border-[#0000FF] bg-[#0000FF]/5" : "border-black/10"}`}>
                      <input type="checkbox" checked={on} data-testid={`nl-book-${b.slug}`} onChange={() => set("featured_slugs", on ? d.featured_slugs.filter((x) => x !== b.slug) : [...(d.featured_slugs || []), b.slug])} className="w-4 h-4" />
                      <span className="truncate">{b.shortTitle} <span className="text-slate-400">({b.status})</span></span>
                    </label>
                  );
                })}
              </div>
            </Field>

            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Font"><select className={inputCls} data-testid="nl-font" value={fmt.font || FONTS[0].value} onChange={(e) => setFmt("font", e.target.value)}>{FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}</select></Field>
              <Field label="Text size"><select className={inputCls} data-testid="nl-size" value={fmt.size || "15"} onChange={(e) => setFmt("size", e.target.value)}>{SIZES.map((x) => <option key={x} value={x}>{x}px</option>)}</select></Field>
              <Field label="Alignment"><select className={inputCls} data-testid="nl-align" value={fmt.align || "left"} onChange={(e) => setFmt("align", e.target.value)}>{ALIGNS.map((x) => <option key={x} value={x}>{x}</option>)}</select></Field>
            </div>
          </>
        )}

        <div className="flex flex-wrap gap-3 pt-2 border-t-2 border-black/5">
          {d.type === "recurring" && <button onClick={regenerate} disabled={busy} data-testid="nl-regenerate" className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-[#00A000] text-[#00A000] px-5 font-bold hover:bg-[#00A000] hover:text-white disabled:opacity-60"><Sparkles size={16} /> Regenerate</button>}
          <button onClick={save} disabled={busy} data-testid="nl-save" className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-black px-5 font-bold hover:bg-[#FBEF14] disabled:opacity-60"><Save size={16} /> Save</button>
          <button onClick={openPreview} data-testid="nl-preview" className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-black/15 px-5 font-bold hover:bg-[#FBEF14]"><Eye size={16} /> Final Preview</button>
          <button onClick={sendNow} disabled={busy} data-testid="nl-send-now" className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#FF0000] text-white px-6 font-bold hover:bg-[#d60000] disabled:opacity-60"><Send size={16} /> Approve &amp; Send</button>
          <button onClick={() => setSchedOpen((o) => !o)} data-testid="nl-schedule-toggle" className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#0000FF] text-white px-5 font-bold hover:bg-[#0000d6]"><Calendar size={16} /> Schedule</button>
        </div>
        {schedOpen && (
          <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-4">
            <Field label="Send on"><input type="datetime-local" className={inputCls} data-testid="nl-schedule-at" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} /></Field>
            <button onClick={schedule} disabled={busy} data-testid="nl-schedule-confirm" className="inline-flex items-center gap-2 min-h-[46px] rounded-full bg-[#0000FF] text-white px-5 font-bold hover:bg-[#0000d6] disabled:opacity-60"><Clock size={16} /> Approve &amp; Schedule</button>
          </div>
        )}
      </div>
      {preview && <PreviewModal subject={preview.subject} html={preview.html} onClose={() => setPreview(null)} />}
    </div>
  );
}

/* ---------- Recurring ---------- */
function RecurringPanel({ books, handleErr }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [paused] = useRecurringPaused();

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/newsletter/list`, authHeader()); setList(data.filter((x) => x.type === "recurring")); }
    catch (e) { handleErr(e); }
  }, [handleErr]);
  useEffect(() => { load(); }, [load]);

  const newCycle = async () => { setBusy(true); try { await axios.post(`${API}/api/admin/newsletter/recurring/new`, {}, authHeader()); toast.success("New cycle with proposed topics created."); load(); } catch (e) { handleErr(e); } finally { setBusy(false); } };
  const approveTopic = async (id, topic) => {
    if (!topic.trim()) return toast.error("Pick or type a topic.");
    setBusy(true);
    try { const { data } = await axios.post(`${API}/api/admin/newsletter/item/${id}/approve-topic`, { topic }, authHeader()); toast.success("Topic approved. Draft created."); setEditing(data); load(); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const retry = async (id) => {
    try {
      const { data } = await axios.post(`${API}/api/admin/newsletter/item/${id}/send`, {}, authHeader());
      if (data.status === "partial") toast.error(`Partially sent: ${data.error_reason || `${data.failed_count} recipient(s) failed.`}`);
      else toast.success(`Sent to ${data.success_count ?? "your"} subscriber(s).`);
      load();
    }
    catch (e) { handleErr(e); load(); }
  };
  const cancel = async (id) => { if (!window.confirm("Cancel this cycle?")) return; try { await axios.post(`${API}/api/admin/newsletter/item/${id}/cancel`, {}, authHeader()); load(); } catch (e) { handleErr(e); } };

  if (editing) return <DraftEditor item={editing} books={books} onBack={() => setEditing(null)} reload={load} handleErr={handleErr} />;

  return (
    <div className="space-y-5" data-testid="nl-recurring">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-slate-600">Cycles are proposed automatically on your schedule. Nothing sends without your approval.</p>
        <div className="flex items-center gap-3">
          <RecurringStatusPill paused={paused} />
          <button onClick={newCycle} disabled={busy} data-testid="nl-new-cycle" className="inline-flex items-center gap-2 min-h-[44px] rounded-full bg-[#0000FF] text-white px-5 font-bold hover:bg-[#0000d6] disabled:opacity-60"><Plus size={16} /> Start a cycle now</button>
        </div>
      </div>
      {list.length === 0 ? <p className="text-slate-500 py-10 text-center bg-white rounded-2xl border-2 border-black/10">No cycles yet.</p> : list.map((c) => (
        <div key={c.id} className="bg-white rounded-2xl border-2 border-black/10 p-5" data-testid={`nl-cycle-${c.id}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="rounded-full bg-[#FBEF14] px-3 py-1 text-xs font-bold">{c.status.replace("_", " ")}</span>
            <span className="text-xs text-slate-500">{formatAdminDateTime(c.created_at)}</span>
          </div>
          {c.status === "topic_proposal" ? (
            <div className="space-y-2">
              <p className="font-heading font-bold">Choose a topic:</p>
              {(c.proposed_topics || []).map((t, i) => (
                <button key={i} onClick={() => approveTopic(c.id, t.topic)} disabled={busy} data-testid={`nl-topic-choice-${i}`} className="w-full text-left rounded-xl border-2 border-black/10 p-3 hover:border-[#0000FF] disabled:opacity-60">
                  <span className="font-bold">{t.topic}</span>
                  {t.summary && <span className="block text-sm text-slate-500 mt-1">{t.summary}</span>}
                </button>
              ))}
              <div className="flex gap-2 pt-2">
                <input className={inputCls} placeholder="Or type your own topic for this cycle" data-testid="nl-custom-topic" value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} />
                <button onClick={() => approveTopic(c.id, customTopic)} disabled={busy} className="rounded-xl bg-[#0000FF] text-white px-4 font-bold hover:bg-[#0000d6]">Use</button>
              </div>
            </div>
          ) : c.status === "sent" ? (
            <p className="font-bold text-[#00A000]">Sent: {c.subject}</p>
          ) : c.status === "canceled" ? (
            <p className="text-slate-400">Canceled</p>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold">{c.subject || "Draft in progress"}</p>
                {c.scheduled_for && <p className="text-sm text-[#0000FF] font-bold">Scheduled: {formatAdminDateTime(c.scheduled_for)}</p>}
                {(c.status === "failed" || c.status === "partial") && c.send_error && (
                  <p className="text-xs text-red-600 font-bold mt-1" data-testid={`nl-cycle-error-${c.id}`}>⚠ {c.send_error}</p>
                )}
              </div>
              <div className="flex gap-2">
                {(c.status === "failed" || c.status === "partial") && (
                  <button onClick={() => retry(c.id)} data-testid={`nl-cycle-retry-${c.id}`} className="rounded-full border-2 border-red-300 text-red-600 px-4 py-2 text-sm font-bold hover:bg-red-50">Retry</button>
                )}
                <button onClick={() => setEditing(c)} data-testid={`nl-edit-cycle-${c.id}`} className="rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-[#FBEF14]">Open</button>
                <button onClick={() => cancel(c.id)} className="rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-red-50 text-red-500">Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Manual ---------- */
function ManualPanel({ books, handleErr }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [paused] = useRecurringPaused();
  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/newsletter/list`, authHeader()); setList(data.filter((x) => x.type === "manual")); }
    catch (e) { handleErr(e); }
  }, [handleErr]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { const { data } = await axios.post(`${API}/api/admin/newsletter/manual/new`, { subject: "", intro: "", sections: [], featured_slugs: [], formatting: {} }, authHeader()); setEditing(data); load(); }
    catch (e) { handleErr(e); }
  };
  const cancel = async (id) => { if (!window.confirm("Delete this draft?")) return; try { await axios.post(`${API}/api/admin/newsletter/item/${id}/cancel`, {}, authHeader()); load(); } catch (e) { handleErr(e); } };
  const retry = async (id) => {
    try { const { data } = await axios.post(`${API}/api/admin/newsletter/item/${id}/send`, {}, authHeader()); toast.success(`Sent to ${data.success_count ?? "your"} subscriber(s).`); load(); }
    catch (e) { handleErr(e); load(); }
  };

  if (editing) return <DraftEditor item={editing} books={books} onBack={() => setEditing(null)} reload={load} handleErr={handleErr} />;
  return (
    <div className="space-y-5" data-testid="nl-manual">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-slate-600">Build a one-off newsletter by hand, with optional AI assist per block.</p>
        <div className="flex items-center gap-3">
          <RecurringStatusPill paused={paused} />
          <button onClick={create} data-testid="nl-manual-new" className="inline-flex items-center gap-2 min-h-[44px] rounded-full bg-[#0000FF] text-white px-5 font-bold hover:bg-[#0000d6]"><Plus size={16} /> New manual newsletter</button>
        </div>
      </div>
      {list.length === 0 ? <p className="text-slate-500 py-10 text-center bg-white rounded-2xl border-2 border-black/10">No manual drafts yet.</p> : list.map((m) => (
        <div key={m.id} className="bg-white rounded-2xl border-2 border-black/10 p-5 flex items-center justify-between" data-testid={`nl-manual-${m.id}`}>
          <div>
            <p className="font-bold">{m.subject || "Untitled draft"}</p>
            <p className="text-xs text-slate-500">{m.status}{m.scheduled_for ? ` · scheduled ${formatAdminDateTime(m.scheduled_for)}` : ""}</p>
            {(m.status === "failed" || m.status === "partial") && m.send_error && (
              <p className="text-xs text-red-600 font-bold mt-1" data-testid={`nl-manual-error-${m.id}`}>⚠ {m.send_error}</p>
            )}
          </div>
          <div className="flex gap-2">
            {(m.status === "failed" || m.status === "partial") && (
              <button onClick={() => retry(m.id)} data-testid={`nl-manual-retry-${m.id}`} className="rounded-full border-2 border-red-300 text-red-600 px-4 py-2 text-sm font-bold hover:bg-red-50">Retry</button>
            )}
            {m.status !== "sent" && <button onClick={() => setEditing(m)} className="rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-[#FBEF14]">Open</button>}
            {m.status !== "sent" && <button onClick={() => cancel(m.id)} className="rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-red-50 text-red-500">Delete</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Archive ---------- */
function ArchiveView({ item, onClose, onRetry }) {
  const rc = item.recipient_count || 0;
  const Stat = ({ label, value, sub }) => (
    <div className="rounded-2xl border-2 border-black/10 bg-[#FFFBF2] p-4 text-center">
      <div className="text-slate-500 font-bold text-xs uppercase tracking-wide">{label}</div>
      <div className="font-heading font-bold text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="nl-archive-view">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-bold text-2xl">{item.subject}</h2>
            <p className="text-slate-500 text-sm mt-1">{item.type} · {formatAdminDateTime(item.sent_at)}</p>
          </div>
          <button onClick={onClose} data-testid="nl-archive-view-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>
        {(item.status === "failed" || item.status === "partial") && (
          <div data-testid="nl-archive-view-status-banner" className="rounded-2xl border-2 border-red-500 bg-red-50 p-4 mb-4 flex items-start justify-between gap-3">
            <div>
              <p className="font-bold text-red-700">{item.status === "failed" ? "Send failed - no email was delivered" : "Partially sent - some recipients did not receive it"}</p>
              {item.error_reason && <p className="text-sm text-red-600 mt-0.5">{item.error_reason}</p>}
            </div>
            {item.newsletter_id && (
              <button onClick={() => onRetry(item.newsletter_id)} data-testid="nl-archive-view-retry" className="rounded-full border-2 border-red-300 text-red-600 px-4 py-2 text-sm font-bold hover:bg-red-100 shrink-0">Retry</button>
            )}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="nl-archive-stats">
          <Stat label="Recipients" value={rc} />
          <Stat label="Open rate" value={`${item.open_rate || 0}%`} sub={`${item.unique_opens || 0} of ${rc} · ${item.opens || 0} total`} />
          <Stat label="Click rate" value={`${item.click_rate || 0}%`} sub={`${item.unique_clicks || 0} of ${rc} · ${item.clicks || 0} total`} />
        </div>
        {(item.link_clicks || []).length > 0 && (
          <div className="mb-4 rounded-2xl border-2 border-black/10 p-4">
            <p className="font-heading font-bold text-sm mb-2">Top links</p>
            <ul className="space-y-1 text-sm">
              {item.link_clicks.map((l, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="truncate text-[#0000FF]">{l.url}</span>
                  <span className="font-bold shrink-0">{l.clicks} click{l.clicks === 1 ? "" : "s"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <iframe title="snapshot" srcDoc={item.html} className="w-full h-[55vh] rounded-2xl border-2 border-black/10 bg-white" data-testid="nl-archive-frame" />
      </div>
    </div>
  );
}

function ArchivePanel({ handleErr }) {
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/newsletter/archive`, authHeader()); setList(data); }
    catch (e) { handleErr(e); }
  }, [handleErr]);
  useEffect(() => { load(); }, [load]);
  const open = async (id) => { try { const { data } = await axios.get(`${API}/api/admin/newsletter/archive/${id}`, authHeader()); setView(data); } catch (e) { handleErr(e); } };
  const retry = async (nlId) => {
    try { const { data } = await axios.post(`${API}/api/admin/newsletter/item/${nlId}/send`, {}, authHeader()); toast.success(`Sent to ${data.success_count ?? "your"} subscriber(s).`); load(); }
    catch (e) { handleErr(e); load(); }
  };
  const TYPE = { welcome: "bg-[#00EE00] text-black", recurring: "bg-[#0000FF] text-white", manual: "bg-[#FBEF14] text-black" };
  const STATUS = { sent: "bg-[#00A000] text-white", partial: "bg-orange-400 text-white", failed: "bg-red-600 text-white", sending: "bg-slate-300 text-black" };
  const Rate = ({ rate, uniq, rc }) => (
    <div className="flex items-center gap-2">
      <span className="font-bold">{rate || 0}%</span>
      <span className="w-16 h-1.5 rounded-full bg-slate-200 overflow-hidden hidden sm:inline-block"><span className="block h-full bg-[#00A000]" style={{ width: `${Math.min(100, rate || 0)}%` }} /></span>
      <span className="text-xs text-slate-400">{uniq || 0}/{rc || 0}</span>
    </div>
  );
  return (
    <div data-testid="nl-archive">
      {list.length === 0 ? <p className="text-slate-500 py-10 text-center bg-white rounded-2xl border-2 border-black/10">Nothing sent yet.</p> : (
        <div className="overflow-x-auto rounded-3xl border-2 border-black/10 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#FFFBF2] border-b-2 border-black/10"><tr><th className="p-4 font-heading">Sent</th><th className="p-4 font-heading">Subject</th><th className="p-4 font-heading">Type</th><th className="p-4 font-heading">Status</th><th className="p-4 font-heading">Recipients</th><th className="p-4 font-heading">Opens</th><th className="p-4 font-heading">Clicks</th><th className="p-4"></th></tr></thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id} className="border-b border-black/5" data-testid={`nl-archive-row-${a.id}`}>
                  <td className="p-4 whitespace-nowrap text-slate-500">{formatAdminDateTime(a.sent_at)}</td>
                  <td className="p-4 font-bold">{a.subject}</td>
                  <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${TYPE[a.type] || "bg-slate-200"}`}>{a.type}</span></td>
                  <td className="p-4" data-testid={`nl-status-${a.id}`}>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS[a.status] || "bg-slate-200 text-black"}`}>{a.status || "sent"}</span>
                    {(a.status === "failed" || a.status === "partial") && a.error_reason && (
                      <p className="text-xs text-red-600 font-bold mt-1 max-w-[220px]" data-testid={`nl-archive-reason-${a.id}`}>⚠ {a.error_reason}</p>
                    )}
                  </td>
                  <td className="p-4">{a.recipient_count}</td>
                  <td className="p-4" data-testid={`nl-opens-${a.id}`}><Rate rate={a.open_rate} uniq={a.unique_opens} rc={a.recipient_count} /></td>
                  <td className="p-4" data-testid={`nl-clicks-${a.id}`}><Rate rate={a.click_rate} uniq={a.unique_clicks} rc={a.recipient_count} /></td>
                  <td className="p-4 flex gap-2">
                    <button onClick={() => open(a.id)} data-testid={`nl-archive-view-${a.id}`} className="rounded-full border-2 border-black/15 px-4 py-1.5 text-xs font-bold hover:bg-[#FBEF14]">View</button>
                    {(a.status === "failed" || a.status === "partial") && a.newsletter_id && (
                      <button onClick={() => retry(a.newsletter_id)} data-testid={`nl-archive-retry-${a.id}`} className="rounded-full border-2 border-red-300 text-red-600 px-4 py-1.5 text-xs font-bold hover:bg-red-50">Retry</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {view && <ArchiveView item={view} onClose={() => setView(null)} onRetry={retry} />}
    </div>
  );
}

/* ---------- Root ---------- */
export default function NewsletterManager({ onAuthError }) {
  const [sub, setSub] = useState("settings");
  const [books, setBooks] = useState([]);
  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) { onAuthError(); return; }
    toast.error(err.response?.data?.detail || "Something went wrong.");
  }, [onAuthError]);

  useEffect(() => { axios.get(`${API}/api/admin/books`, authHeader()).then(({ data }) => setBooks(data)).catch(() => {}); }, []);

  const TABS = [
    ["settings", "Settings", Settings], ["welcome", "Welcome", Mail],
    ["recurring", "Recurring", Repeat], ["manual", "Manual", PenLine], ["archive", "Sent", Archive],
  ];
  return (
    <div data-testid="newsletter-manager">
      <h1 className="font-heading font-bold text-3xl mb-1">Newsletter</h1>
      <p className="text-slate-600 mb-6">Welcome email, recurring engagement newsletter, and manual sends. All CAN-SPAM compliant.</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`nl-tab-${k}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border-2 ${sub === k ? "bg-[#0000FF] text-white border-[#0000FF]" : "bg-white text-slate-600 border-black/10"}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>
      {sub === "settings" && <SettingsPanel handleErr={handleErr} />}
      {sub === "welcome" && <WelcomePanel handleErr={handleErr} />}
      {sub === "recurring" && <RecurringPanel books={books} handleErr={handleErr} />}
      {sub === "manual" && <ManualPanel books={books} handleErr={handleErr} />}
      {sub === "archive" && <ArchivePanel handleErr={handleErr} />}
    </div>
  );
}
